import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import service from '../service';
import type { ToastGuid, UpdateToastGuidPayload } from '../types/toastGuid';
import { toastGuidQueryKeys } from './useToastGuids';

export interface UpdateToastGuidVariables {
  id: string;
  payload: UpdateToastGuidPayload;
}

export function useUpdateToastGuid(
  options?: UseMutationOptions<ToastGuid, Error, UpdateToastGuidVariables>,
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation({
    mutationKey: ['toastGuid', 'update'],
    mutationFn: async ({ id, payload }: UpdateToastGuidVariables) => {
      return service.updateToastGuid(id, payload);
    },
    retry: (failureCount, error: any) => {
      const status =
        error?.status ??
        error?.response?.status ??
        error?.cause?.status ??
        null;
      const isServerOrNetwork = status == null || status >= 500;
      return failureCount < 2 && isServerOrNetwork;
    },
    ...restOptions,
    onSuccess: (data, variables, context, mutation) => {
      queryClient.invalidateQueries({ queryKey: toastGuidQueryKeys.all });
      queryClient.invalidateQueries({
        queryKey: toastGuidQueryKeys.detail(variables.id),
      });
      onSuccess?.(data, variables, context, mutation);
    },
  });
}
